import * as Yup from 'yup'
import React, { useEffect, useMemo, useState } from 'react'
import { Formik } from 'formik'
import { withCapture, _ } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import FormTabs from 'features/forms/FormTabs'
import { useEventSlug, useQuery } from 'hooks'
import { useDispatch } from 'react-redux'
import { GetEventInviteConfigQuery, GetEventInviteConfigQueryVariables } from 'generated/graphql'
import { getChangedValues } from 'features/forms/utils'
import { updateEvent } from 'features/generalsettings/graphql/mutation'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import constants from 'constants/constants'
import Attendees from './tabs/Attendees'
import EmailContent from './tabs/EmailContent'
import { eventInviteConfigQuery } from './graphql/query'
import { sendTestEmail } from './graphql/mutation'
import SplashPage from './SplashPage'
import AttendeeTags from './tabs/AttendeeTags'

const AttendeeValidation = Yup.object().shape({
    inviteMailSubject: Yup.string().required(),
    inviteMailReplyTo: Yup.string().email(),
})

const AttendeeSetting = () => {
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)
    const eventSlug = useEventSlug()
    const [initialValues, setInitialValues] = useState({})
    const { data, requestStatus, fetchData } = useQuery<
        GetEventInviteConfigQuery, GetEventInviteConfigQueryVariables
    >(eventInviteConfigQuery, { eventSlug })
    const isLite = constants.isLite

    useEffect(() => {
        if (data?.event) {
            setInitialValues({
                ...data.event,
                // Set default subject for email content
                inviteMailSubject: data?.event?.inviteMailSubject || 'Invitation',
            })
        }
    }, [data])

    const handleSubmit = withCapture(async (values, { resetForm }) => {
        const isSendTestEmail = values.sendTestEmail
        delete values.sendTestEmail
        setIsUploading(true)
        const submitValues = getChangedValues(initialValues, values)
        if (isLite) {
            submitValues.inviteOnly = true
        }
        const response = await updateEvent(eventSlug, submitValues)
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            resetForm({ values: initialValues })
            return
        }

        resetForm({ values })
        setInitialValues(values)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))
        if (isSendTestEmail) {
            const testEmailResponse = await sendTestEmail(eventSlug)
            if (testEmailResponse.data) {
                dispatch(showNotification({
                    type: 'success',
                    body: _('form.notification.send_test_email_success'),
                }))
            }
        }
    })
    const AttendeesMemo = useMemo(() => () => <Attendees />, [])

    if (requestStatus === 'loading' || requestStatus === 'idle') {
        return <FullPageLoading />
    }

    if (!data?.event?.paid && requestStatus === 'succeeded') {
        return <SplashPage isRequested={data?.event?.isActivationRequested} fetchData={fetchData} />
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={AttendeeValidation}
            enableReinitialize
        >
            <>
                <FormTabs
                    tabs={[
                        {
                            id: 'attendees',
                            name: _('attendee.tabs.attendees'),
                            Component: AttendeesMemo
                        },
                        {
                            id: 'email-content',
                            name: _('attendee.tabs.mail_content'),
                            Component: EmailContent
                        },
                        {
                            id: 'tags',
                            name: _('attendee.tabs.tags_management'),
                            Component: AttendeeTags
                        },
                    ]}
                    showInviteOnly={!isLite}
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default AttendeeSetting
